/* eslint-disable no-param-reassign */
import { convertFontHtml2Obj } from './TextRunsParserUtil';
import { getSolidHexColor, htmlToSdkScale, htmlToSdkFontScale } from './ParserUtils';
import { STShapeType, STTextVerticalType } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import {
    DataType, IShapeData, STTextAlignType, STTextAnchoringType,
    STTextAutonumberScheme, STTextStrikeType, STTextUnderlineType,
} from '@tencent/slide-sdk-model';
import { colorStrToColorDmlA } from '@src/slide/infrastructure/module/independent/color/transform';
import { OFFICE_COPY_NUMBER_TYPE_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/text-auto-number-scheme';

export const DEFAULT_BULLET_INDENT = 349758;
export const DEFAULT_FONT_SIZE = 18;
// 文本解析器 nodes -》 sdk textBody
export default class TextPasteParserV3 {
    public static instance: TextPasteParserV3;
    public static getSingleton = () => {
        if (!TextPasteParserV3.instance) {
            TextPasteParserV3.instance = new TextPasteParserV3();
        }
        return TextPasteParserV3.instance;
    };

    public mSOStyle: any;

    public initParser(mSOStyle: any) {
        this.mSOStyle = mSOStyle;
    }

    public parseNodesToSdkData(nodes: Array<any>) {
        const txBody = this.getTextBodyByNodes(nodes);
        const sdkData = {
            txBody,
            nvPr: {
                txBox: true,
            },
            spPr: {
                xfrm: {
                    off: {
                        x: 0, y: 0,
                    },
                    ext: {
                        cx: 400 * htmlToSdkScale,
                        cy: 400 * htmlToSdkScale,
                    },
                },
            },
            type: DataType.Shape,
        };
        let iframeLink = null;
        nodes.forEach((node) => {
            node.forEach((nodeItem) => {
                if (nodeItem.tagName.toLowerCase() === 'slide-data') {
                    iframeLink = nodeItem.getAttribute('data-src');
                }
            });
        });
        if (iframeLink) {
            const iframeInfo = JSON.parse(iframeLink);
            // // @ts-ignore
            // sdkData.props.addon = {
            //     displayMode: 'iframe',
            //     originUrl: iframeInfo.originUrl,
            //     embedUrl: iframeInfo.embedUrl,
            //     extraData: iframeInfo.extraData,
            //     iframeId: iframeInfo.iframeId,
            //     docElement: iframeInfo.docElement,
            // };
            // @ts-ignore
            sdkData.spPr.prstGeom = {
                prst: STShapeType.rect,
            };
            // @ts-ignore
            sdkData.nvPr.hlinkClick = {
                id: iframeInfo.originUrl,
            };
        }
        return sdkData as IShapeData;
    }

    public getTextBodyByNodes(nodesArray: Array<any>) {
        const txBody: any = {};
        txBody.p = [];
        const { p } = txBody;
        txBody.bodyPr = {};
        const { bodyPr } = txBody;
        bodyPr.lang = 'zh-CN';
        nodesArray.forEach((nodes) => {
            const egTextRuns: any = {
                pPr: {},
                egTextRun: [],
            };
            const { pPr, egTextRun } = egTextRuns;
            const textFontSize = parseInt(nodes?.[1]?.style?.fontSize, 10);
            const textRunNodes = this.reduceBulletProps(nodes, pPr);
            // eslint-disable-next-line complexity
            textRunNodes.forEach((node) => {
                if (node.nodeType !== Node.TEXT_NODE && node.tagName.toLowerCase() === 'li') {
                    // 增加默认的项目符号
                    pPr.algn = STTextAlignType?.l;
                    pPr.buFont = {  // 这里要改，否则从火狐拷贝过来显示样式不对
                        charset: '0',
                        typeface: 'Arial',
                    };
                    pPr.buChar = { charKeyword: '•' };
                    pPr.marL = DEFAULT_BULLET_INDENT;
                }
                if (node.tagName.toLowerCase() === 'span') {
                    let fontSizeRate = 1;
                    if (textFontSize) {
                        fontSizeRate = textFontSize / DEFAULT_FONT_SIZE;
                    }
                    const nodeStyle = node.getAttribute('style') || '';
                    // 项目符号从office粘贴
                    if (/mso-special-format:(\\n)?bullet/.test(nodeStyle)) {
                        this.setBulletStyleFromCopyData(pPr, node, fontSizeRate);
                        pPr.buChar = { charKeyword: node.textContent || '•' };
                        return;
                    }
                    // 数字符号从office粘贴
                    if (/mso-special-format:(\\n)?"numbullet/.test(nodeStyle)) {
                        // 获取数字编号具体类型和起始编号
                        const matches = nodeStyle.match(/mso-special-format:(?:\\n)?"numbullet(\d+)\\,(\d+)/) || [];
                        const type = OFFICE_COPY_NUMBER_TYPE_MAP[matches[1]] || STTextAutonumberScheme?.arabicPeriod;
                        const startAt = matches[2] ? parseInt(matches[2], 10) : null;
                        this.setBulletStyleFromCopyData(pPr, node, fontSizeRate);
                        pPr.buAutoNum = {
                            type,
                        };
                        if (startAt) {
                            pPr.buAutoNum.startAt = startAt;
                        }
                        return;
                    }
                }
                const styleMap = this.mSOStyle.getComputedStyle(node);
                this.setAlignment(styleMap, pPr, bodyPr);
                const comStyle = this.mSOStyle.getComputedStyle;
                const textFormatRunsDeltas: any = convertFontHtml2Obj(node, getSolidHexColor, comStyle);
                textFormatRunsDeltas.forEach((runData) => {
                    const textRunItem: any = {
                        rPr: {},
                    };
                    textRunItem.t = runData.text;
                    this.setRunDataProps1(runData, textRunItem.rPr);
                    this.setRunDataProps2(runData, textRunItem.rPr);
                    // 有些数据是文本节点上的，但是对应的ooxml结构中是放到paragraphProps上
                    this.setRunDataToParagraphProps(runData, pPr);
                    egTextRun.push({ r: textRunItem });
                });
            });
            p.push(egTextRuns);
        });
        return txBody;
    }

    /**
     * 判断是否有项目符号并更新段落属性
     * 如果段落的第一个节点只有一个字符且字体为 Wingdings，就认为是项目符号
     * 设置项目符号属性并删除紧跟的空格字符
     */
    private reduceBulletProps(nodes: HTMLElement[], pPr): HTMLElement[]  {
        const [firstNode] = nodes;
        const firstNodeText = firstNode?.textContent?.trim() || '';
        if (firstNodeText.length !== 1) {
            return nodes;
        }
        if (this.isWordBulletData(firstNode)) {
            pPr.indent = -DEFAULT_BULLET_INDENT;
            pPr.marL = DEFAULT_BULLET_INDENT;
            pPr.buChar = { charKeyword: firstNodeText };
            pPr.buFont = {
                typeface: 'Wingdings',
            };
            let startIndex = 1;
            while (nodes[startIndex]?.textContent?.trim() === '' && startIndex < nodes.length) {
                startIndex += 1;
            }
            return nodes.slice(startIndex);
        }
        return nodes;
    }

    /**
     * 判断当前复制是否是 word 的项目符号，需要满足两个条件
     * 1. word 复制的项目符号 style 中都有 mso-list 标识
     * 2. 当前节点字体为 Wingdings
     * 由于 mso-list 标识是在段落节点上，所以就从当前节点递归遍历父级，找到有 mso-list 标识的即可
     * 字体匹配逻辑也一样，由于离屏 dom 无法获取到继承的样式，递归父级的 style 属性判断 fontFamily 属性即可
     */
    private isWordBulletData(element: HTMLElement) {
        let fontFamilyMatch = false;
        let msoListMatch = false;
        let current: HTMLElement | null = element;
        while (current) {
            fontFamilyMatch = fontFamilyMatch || current.style.fontFamily === 'Wingdings';
            msoListMatch = msoListMatch || current.getAttribute('style')?.includes('mso-list') || false;
            if (msoListMatch && fontFamilyMatch) {
                return true;
            }
            current = current.parentElement;
        }
        return false;
    }

    private setRunDataToParagraphProps(runData, paragraphProps) {
        // 设置行高，转换比例 1:1e5
        if (typeof runData.format.lineHeight === 'number') {
            paragraphProps.lnSpc = paragraphProps.lnSpc || {};
            paragraphProps.lnSpc.spcPct = {
                val: runData.format.lineHeight * 1e5,
            };
        }
    }

    private setAlignment(styleMap, pPr, bodyPr) {
        const textAlignValue = styleMap?.['text-align'];
        if (textAlignValue) {
            pPr.algn = {
                center: STTextAlignType.ctr,
                left: STTextAlignType.l,
                right: STTextAlignType.r,
            }[textAlignValue];
        }

        const verticalValue = styleMap?.['vertical-align'];
        if (verticalValue) {
            bodyPr.anchor = {
                middle: STTextAnchoringType.ctr,
                top: STTextAnchoringType.t,
                bottom: STTextAnchoringType.b,
            }[verticalValue];
            bodyPr.vert = STTextVerticalType.horz;
        }
    }

    private setBulletStyleFromCopyData(pPr, node, fontSizeRate) {
        pPr.buFont = {
            charset: '0',
            typeface: node.style.fontFamily || 'Arial',
        };
        pPr.indent = -DEFAULT_BULLET_INDENT * fontSizeRate;
        pPr.marL = DEFAULT_BULLET_INDENT;
    }

    private setRunDataProps1(runData: any, rPr: any) {
        // 设置字体
        if (runData.format.fontfamily && runData.format.fontfamily.length > 0) {
            rPr.latin = {
                typeface: runData.format.fontfamily,
            };
            rPr.ea = {
                typeface: runData.format.fontfamily,
            };
        }

        // 设置字号
        if (runData.format.fontsize >= 0) {
            rPr.sz = parseInt(runData.format.fontsize, 10) * 100;
        }

        // 设置字的颜色
        if (runData.format.fontcolor && runData.format.fontcolor.length > 0) {
            rPr.solidFill = {
                egColorChoice: colorStrToColorDmlA(runData.format.fontcolor),
            };
        }

        // 设置字的背景色
        if (runData.format.highlight && runData.format.highlight.length > 0) {
            rPr.highlight = colorStrToColorDmlA(runData.format.highlight);
        }
    }

    private setRunDataProps2(runData: any, rPr: any) {
        const checkAttributeContainValues = attribute => attribute && attribute.length > 0;

        // 下划线
        if (checkAttributeContainValues(runData.format.underline)) {
            if (runData.format.underline === 'underline') {
                rPr.u = STTextUnderlineType.sng;
            }
        }

        // 删除线
        if (checkAttributeContainValues(runData.format.strikeline)) {
            if (runData.format.strikeline === 'strikeline') {
                rPr.strike = STTextStrikeType?.sngStrike;
            }
        }

        // 斜体
        if (runData.format.italic) {
            rPr.i = true;
        }

        // 粗体
        if (runData.format.bold) {
            rPr.b = true;
        }

        // 超链接
        if (checkAttributeContainValues(runData.format.url)) {
            rPr.hlinkClick = {
                id: runData.format.url,
            };
        }

        // 文字间距
        if (runData.format.spc) {
            rPr.spc = runData.format.spc * htmlToSdkFontScale;
        }
    }
}
